import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddCommentOutlined';
import { SectionCard } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { useFeature } from '../featureFlags/FeatureGate';
import { useHasAnyPermission, useHasPermission } from '../PermissionGate';
import {
  FEATURE_FTL_NOTES,
  createFtlNote,
  fetchOrderNotes,
  notifyFtlNotesChanged,
  toErrorMessage,
  type FtlNote,
  type OrderNotes,
} from './ftlNotesApi';

/**
 * „Uwagi dla CS” na karcie zlecenia (makieta back office v2, BR-FTL-13
 * część, ścieżka FTL-05; 20.10.2026, przełącznik `ftlNotes`).
 *
 * Planista albo Team Leader (planowanie FTL) wysyła uwagę dla przyjęcia
 * zleceń — kategoria z listy roboczej, treść, „pilna”. Odbiorca (do czasu
 * działu CS: przyjęcie zleceń) widzi ją w „Uwagach od FTL” i zmienia stan
 * z komentarzem; tutaj obie strony widzą stan i ostatni komentarz. Bez
 * poczty — komunikacja na rekordzie. Wyłączony przełącznik albo konto bez
 * planowania FTL i przyjęcia zleceń — sekcji nie ma.
 */

/** Kolor stanu — kod z API; nieznany kod szary. */
export const noteStatusColor = (status: string): 'warning' | 'info' | 'success' | 'default' =>
  status === 'new' ? 'warning' : status === 'in_progress' ? 'info' : status === 'resolved' ? 'success' : 'default';

/** Jedna uwaga: stan, kategoria, treść, kto i kiedy, ostatni komentarz odbiorcy. */
export const NoteSummary = ({ note }: { note: FtlNote }) => (
  <Box data-testid="ftl-note" sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Chip size="small" color={noteStatusColor(note.status)} label={note.statusLabel} />
      <Chip size="small" variant="outlined" label={note.categoryLabel} />
      {note.urgent ? <Chip size="small" color="error" variant="outlined" label="pilna" /> : null}
      <Typography variant="caption" color="text.secondary">
        {[note.createdBy?.name, dateTimeNoSeconds(note.createdAt)].filter(Boolean).join(' · ')}
      </Typography>
    </Stack>
    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {note.content}
    </Typography>
    {note.statusComment ? (
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.25 }}>
        {statusCommentText(note)}
      </Typography>
    ) : null}
  </Box>
);

/** „załatwiona — Klient poinformowany (Anna Lis, 20.10.2026, 11:00)”. */
export function statusCommentText(note: FtlNote): string {
  const who = [
    note.statusChangedBy?.name,
    note.statusChangedAt ? dateTimeNoSeconds(note.statusChangedAt) : null,
  ]
    .filter(Boolean)
    .join(', ');
  return `${note.statusLabel} — ${note.statusComment ?? ''}${who ? ` (${who})` : ''}`;
}

/** Okno nowej uwagi — kategoria ze słownika z API, treść obowiązkowa. */
const NewNoteDialog = ({
  open,
  categories,
  onClose,
  onSent,
  userOfferId,
}: {
  open: boolean;
  categories: OrderNotes['categories'];
  onClose: () => void;
  onSent: () => void;
  userOfferId: number;
}) => {
  const notify = useNotify();
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCategory('');
    setContent('');
    setUrgent(false);
  };

  const send = () =>
    void (async () => {
      setBusy(true);
      try {
        await createFtlNote({ userOfferId, category, content, urgent });
        notify('Uwaga wysłana do przyjęcia zleceń', { type: 'success' });
        reset();
        onSent();
      } catch (e) {
        notify(toErrorMessage(e), { type: 'error' });
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Uwaga dla CS</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Uwaga trafi do przyjęcia zleceń (CS i Administracja) na liście „Uwagi od FTL”. Nic nie wychodzi pocztą
          ani do klienta.
        </DialogContentText>
        <Stack spacing={2}>
          <TextField
            select
            size="small"
            label="Kategoria"
            value={category}
            disabled={busy}
            onChange={(e) => setCategory(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'ftl-note-category' } }}
          >
            {categories.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Treść"
            multiline
            minRows={3}
            value={content}
            disabled={busy}
            onChange={(e) => setContent(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'ftl-note-content' } }}
          />
          <FormControlLabel
            control={<Checkbox checked={urgent} disabled={busy} onChange={(e) => setUrgent(e.target.checked)} />}
            label="Pilna"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Anuluj
        </Button>
        <Button
          variant="contained"
          onClick={send}
          // Bez kategorii i treści API i tak odmówi — operator ma to widzieć od razu.
          disabled={busy || category === '' || content.trim() === ''}
          // Kontrakt `data-testid` paczki (S-22.10-02): zatwierdzenie okna.
          data-testid="dlg-ok"
        >
          Wyślij uwagę
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NotesBody = ({ record }: { record: RaRecord }) => {
  const refresh = useRefresh();
  const canWrite = useHasPermission('FTL_PLANNING');
  const [state, setState] = useState<OrderNotes | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const id = Number(record.id);

  const load = useCallback(async () => {
    try {
      setState(await fetchOrderNotes(id));
      setError(null);
    } catch (e) {
      setError(toErrorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <SectionCard title="Uwagi dla CS">
      <Stack spacing={1} data-testid="ftl-notes-section">
        {canWrite ? (
          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCommentIcon />}
              disabled={state === null}
              onClick={() => setDialogOpen(true)}
              data-testid="ftl-zlecenie-uwaga-cs"
            >
              Uwaga dla CS
            </Button>
          </Box>
        ) : null}
        {error ? <Alert severity="warning">{error}</Alert> : null}
        {state === null && !error ? <CircularProgress size={18} /> : null}
        {state && state.notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak uwag przy tym zleceniu.
          </Typography>
        ) : null}
        {state?.notes.map((n) => <NoteSummary key={n.id} note={n} />)}
      </Stack>
      {state ? (
        <NewNoteDialog
          open={dialogOpen}
          categories={state.categories}
          userOfferId={id}
          onClose={() => setDialogOpen(false)}
          onSent={() => {
            setDialogOpen(false);
            void load();
            notifyFtlNotesChanged();
            // Wpis w historii zmian zlecenia — karta pokazuje go po odświeżeniu.
            refresh();
          }}
        />
      ) : null}
    </SectionCard>
  );
};

/** Sekcja na karcie zlecenia — za przełącznikiem i dla obu stron uwagi. */
export const FtlNotesSection = () => {
  const record = useRecordContext<RaRecord>();
  const on = useFeature(FEATURE_FTL_NOTES);
  const allowed = useHasAnyPermission(['FTL_PLANNING', 'ORDER_INTAKE']);
  if (!on || !allowed || !record) return null;
  return <NotesBody record={record} />;
};
